/**
 * The combat HUD view (plan G10, spec §10): a DOM/CSS overlay built once at init. Per frame it only writes
 * `transform`, `opacity`, `textContent`, one SVG `stroke-dashoffset` and a few `data-*` state attributes on
 * cached elements, and it never reads layout, so it can never force a reflow. Every value is diffed against the
 * last value written (quantised to what is visible: 0.1 px, 0.01 opacity), so a steady HUD writes nothing.
 *
 * Friend and foe (style guide §9.5): hostile target brackets are a diamond of chevrons in the warm hostile
 * colour; friendly ones are square corners in the HUD cyan. Contacts, arrows and damage wedges also differ by
 * shape (and weight), never by red versus green alone.
 *
 * Allocation note (D-014): strings for DOM writes are built only when a quantised value changes; that is the
 * unavoidable cost of writing the DOM, and a still HUD allocates nothing.
 */
import type { HudTuning } from '../features/hud/tuning'
import { formatHudSpeed, HIT, HUD_READOUTS, hudSpeedKey, STANCE_GLYPHS, VV, WEDGE_COUNT, type HudState } from './hudModel'
import { formatHudDistance, formatSpeed, hudDistanceKey, speedKey } from './reach/format'

/** True in test builds (`npm run build:test`, vitest): the camera debug tag stays visible there (H5). */
const IS_TEST_BUILD = import.meta.env.MODE === 'test'

/**
 * Whether the camera debug tag ("CHASE · WEBGPU") shows (dashboard spec H5): only with the F3 perf overlay open
 * (`#perf`, created by boot after the features' init, so it is looked up until found) or in test builds.
 * Per frame it reads one cached element's `hidden` property: no layout, no allocation.
 */
export function createDebugTagProbe(): () => boolean {
  let perf: HTMLElement | null = null
  return (): boolean => {
    if (IS_TEST_BUILD) return true
    if (perf === null) {
      if (typeof document === 'undefined') return false
      perf = document.getElementById('perf')
      if (perf === null) return false
    }
    return !perf.hidden
  }
}

/** The subset of `CSSStyleDeclaration` the view writes. */
export interface HudStyle {
  transform: string
  opacity: string
  strokeDashoffset: string
}

/** The subset of an element the view writes (tests pass fakes that count writes). */
export interface HudNode {
  textContent: string | null
  readonly style: HudStyle
  setAttribute(name: string, value: string): void
}

/** Every element the view writes, created once by `mountHud`. */
export interface HudElements {
  /** Whole-HUD state: `data-alive`, `data-view` (chase/cockpit). */
  root: HudNode
  stickDot: HudNode
  boresight: HudNode
  /** Aim assist circle around the gun cross (`data-on`, `data-engaged`). */
  assist: HudNode
  hit: HudNode
  velocity: HudNode
  lead: HudNode
  target: HudNode
  /** Bracket pieces: top-left, top-right, bottom-right, bottom-left (friendly) or top, right, bottom, left (hostile). */
  corners: HudNode[]
  lockRing: HudNode
  lockArc: HudNode
  targetInfo: HudNode
  targetDistance: HudNode
  targetShield: HudNode
  targetHull: HudNode
  arrow: HudNode
  arrowHead: HudNode
  arrowDistance: HudNode
  contacts: HudNode[]
  wedges: HudNode[]
  shieldFill: HudNode
  shieldValue: HudNode
  hullBar: HudNode
  hullFill: HudNode
  hullValue: HudNode
  energyBar: HudNode
  energyFill: HudNode
  energyValue: HudNode
  speed: HudNode
  fa: HudNode
  weaponSlot: HudNode
  weaponName: HudNode
  secondary: HudNode
  torpedoes: HudNode
  lockStatus: HudNode
  wave: HudNode
  enemies: HudNode
  banner: HudNode
  respawn: HudNode
  /** Camera debug tag (`data-on`: only with the perf overlay or in test builds, H5). */
  camera: HudNode
  /** Speed unit, from the same formatter as the numeral (H1). */
  speedUnit: HudNode
  /** "Chase camera" / "Cockpit" toast after a camera switch (H5). */
  cameraToast: HudNode
  /** Target card under the bracket (H4): name, class · stance glyph + faction, closing speed. */
  targetName: HudNode
  targetMeta: HudNode
  targetClosing: HudNode
  /** Compact target line in the weapons block: "TGT <name> · 700 m" (`data-on`). */
  tgt: HudNode
  tgtName: HudNode
  tgtDistance: HudNode
}

/** Stick-dot travel for a full stick deflection (CSS px); matches the ring in hud.css. */
export const STICK_RADIUS_PX = 60
/** Lock ring circle radius in its 100 × 100 SVG, and its circumference (the dash length). */
const LOCK_R = 44
const LOCK_C = 2 * Math.PI * LOCK_R
/** Target info block offset below the bracket (px). */
const INFO_GAP_PX = 10
const RAD_TO_DEG = 180 / Math.PI
/** Mid radius of the wedge arc in its SVG (user units = px at scale 1). */
const WEDGE_ART_RADIUS = 146.5

/** One diffed write target: a node plus the last values written to one of its properties. */
interface Channel {
  node: HudNode
  a: number
  b: number
  c: number
  s: string
}

/** The assist circle's radius in its 100 px SVG (scaled to the cone's on-screen radius). */
const ASSIST_SVG_RADIUS = 48
/** `data-assist` / `data-engaged` values by `HudTargetState.assist`. */
const ASSIST_NAMES = ['0', '1', '2'] as const

function channel(node: HudNode): Channel {
  return { node, a: NaN, b: NaN, c: NaN, s: '\u0000' }
}

export interface HudView {
  el: HudElements
  /** Transform channels. */
  stick: Channel
  boresight: Channel
  assist: Channel
  hit: Channel
  velocity: Channel
  lead: Channel
  target: Channel
  corners: Channel[]
  lockRing: Channel
  targetInfo: Channel
  targetShield: Channel
  targetHull: Channel
  arrow: Channel
  arrowHead: Channel
  contacts: Channel[]
  wedges: Channel[]
  shieldFill: Channel
  hullFill: Channel
  energyFill: Channel
  /** Opacity channels. */
  hitOpacity: Channel
  wedgeOpacity: Channel[]
  bannerOpacity: Channel
  /** stroke-dashoffset. */
  lockArc: Channel
  /** Text channels. */
  targetDistance: Channel
  arrowDistance: Channel
  shieldValue: Channel
  hullValue: Channel
  energyValue: Channel
  speed: Channel
  fa: Channel
  weaponSlot: Channel
  weaponName: Channel
  torpedoes: Channel
  lockStatus: Channel
  wave: Channel
  enemies: Channel
  banner: Channel
  respawn: Channel
  camera: Channel
  speedUnit: Channel
  cameraToast: Channel
  cameraToastOpacity: Channel
  targetName: Channel
  /** Target meta line: `a` = the stance and `s` = the faction it was built from; the class in `metaCls`. */
  targetMeta: Channel
  metaCls: string
  targetClosing: Channel
  tgtName: Channel
  tgtDistance: Channel
  /** Attribute channels (`data-*`). */
  attrs: {
    alive: Channel
    view: Channel
    hitKind: Channel
    velocityMode: Channel
    leadOn: Channel
    leadGimbal: Channel
    leadAssist: Channel
    assistOn: Channel
    assistEngaged: Channel
    targetOn: Channel
    targetHostile: Channel
    lock: Channel
    arrowOn: Channel
    arrowHostile: Channel
    contactOn: Channel[]
    contactHostile: Channel[]
    wedgeKind: Channel[]
    hullLow: Channel
    boost: Channel
    faOn: Channel
    secondaryOn: Channel
    lockStatus: Channel
    bannerOn: Channel
    respawnOn: Channel
    cameraOn: Channel
    cameraToastOn: Channel
    tgtOn: Channel
  }
  /** Shows the camera debug tag this frame (H5). */
  debugTag: () => boolean
  /** Camera-mode labels, built once (with the backend name). */
  chaseLabel: string
  cockpitLabel: string
}

/** `debugTag` decides when the camera debug tag shows (default: the perf overlay is open, or a test build; H5). */
export function createHudView(el: HudElements, backend: string, debugTag: () => boolean = createDebugTagProbe()): HudView {
  const many = (nodes: HudNode[]): Channel[] => nodes.map(channel)
  return {
    el,
    stick: channel(el.stickDot),
    boresight: channel(el.boresight),
    assist: channel(el.assist),
    hit: channel(el.hit),
    velocity: channel(el.velocity),
    lead: channel(el.lead),
    target: channel(el.target),
    corners: many(el.corners),
    lockRing: channel(el.lockRing),
    targetInfo: channel(el.targetInfo),
    targetShield: channel(el.targetShield),
    targetHull: channel(el.targetHull),
    arrow: channel(el.arrow),
    arrowHead: channel(el.arrowHead),
    contacts: many(el.contacts),
    wedges: many(el.wedges),
    shieldFill: channel(el.shieldFill),
    hullFill: channel(el.hullFill),
    energyFill: channel(el.energyFill),
    hitOpacity: channel(el.hit),
    wedgeOpacity: many(el.wedges),
    bannerOpacity: channel(el.banner),
    lockArc: channel(el.lockArc),
    targetDistance: channel(el.targetDistance),
    arrowDistance: channel(el.arrowDistance),
    shieldValue: channel(el.shieldValue),
    hullValue: channel(el.hullValue),
    energyValue: channel(el.energyValue),
    speed: channel(el.speed),
    fa: channel(el.fa),
    weaponSlot: channel(el.weaponSlot),
    weaponName: channel(el.weaponName),
    torpedoes: channel(el.torpedoes),
    lockStatus: channel(el.lockStatus),
    wave: channel(el.wave),
    enemies: channel(el.enemies),
    banner: channel(el.banner),
    respawn: channel(el.respawn),
    camera: channel(el.camera),
    speedUnit: channel(el.speedUnit),
    cameraToast: channel(el.cameraToast),
    cameraToastOpacity: channel(el.cameraToast),
    targetName: channel(el.targetName),
    targetMeta: channel(el.targetMeta),
    metaCls: '\u0000',
    targetClosing: channel(el.targetClosing),
    tgtName: channel(el.tgtName),
    tgtDistance: channel(el.tgtDistance),
    attrs: {
      alive: channel(el.root),
      view: channel(el.root),
      hitKind: channel(el.hit),
      velocityMode: channel(el.velocity),
      leadOn: channel(el.lead),
      leadGimbal: channel(el.lead),
      leadAssist: channel(el.lead),
      assistOn: channel(el.assist),
      assistEngaged: channel(el.assist),
      targetOn: channel(el.target),
      targetHostile: channel(el.target),
      lock: channel(el.target),
      arrowOn: channel(el.arrow),
      arrowHostile: channel(el.arrow),
      contactOn: many(el.contacts),
      contactHostile: many(el.contacts),
      wedgeKind: many(el.wedges),
      hullLow: channel(el.hullBar),
      boost: channel(el.energyBar),
      faOn: channel(el.fa),
      secondaryOn: channel(el.secondary),
      lockStatus: channel(el.lockStatus),
      bannerOn: channel(el.banner),
      respawnOn: channel(el.respawn),
      cameraOn: channel(el.camera),
      cameraToastOn: channel(el.cameraToast),
      tgtOn: channel(el.tgt),
    },
    debugTag,
    chaseLabel: `CHASE · ${backend.toUpperCase()}`,
    cockpitLabel: `COCKPIT · ${backend.toUpperCase()}`,
  }
}

// ---- Diffed writers -------------------------------------------------------------------------------------------

function q10(v: number): number {
  return Math.round(v * 10) / 10
}

function q100(v: number): number {
  return Math.round(v * 100) / 100
}

function translate(ch: Channel, x: number, y: number): void {
  const qx = q10(x)
  const qy = q10(y)
  if (qx === ch.a && qy === ch.b) return
  ch.a = qx
  ch.b = qy
  ch.node.style.transform = `translate3d(${qx}px,${qy}px,0)`
}

function translateScale(ch: Channel, x: number, y: number, s: number): void {
  const qx = q10(x)
  const qy = q10(y)
  const qs = q100(s)
  if (qx === ch.a && qy === ch.b && qs === ch.c) return
  ch.a = qx
  ch.b = qy
  ch.c = qs
  ch.node.style.transform = `translate3d(${qx}px,${qy}px,0) scale(${qs})`
}

/** Translate then rotate (degrees, quantised to 0.1°). */
function translateRotate(ch: Channel, x: number, y: number, deg: number): void {
  const qx = q10(x)
  const qy = q10(y)
  const qd = q10(deg)
  if (qx === ch.a && qy === ch.b && qd === ch.c) return
  ch.a = qx
  ch.b = qy
  ch.c = qd
  ch.node.style.transform = `translate3d(${qx}px,${qy}px,0) rotate(${qd}deg)`
}

function rotate(ch: Channel, deg: number): void {
  const qd = q10(deg)
  if (qd === ch.a) return
  ch.a = qd
  ch.node.style.transform = `rotate(${qd}deg)`
}

function rotateScale(ch: Channel, deg: number, s: number): void {
  const qd = q10(deg)
  const qs = q100(s)
  if (qd === ch.a && qs === ch.b) return
  ch.a = qd
  ch.b = qs
  ch.node.style.transform = `rotate(${qd}deg) scale(${qs})`
}

function scale(ch: Channel, s: number): void {
  const qs = Math.round(s * 1000) / 1000
  if (qs === ch.a) return
  ch.a = qs
  ch.node.style.transform = `scale(${qs})`
}

function scaleX(ch: Channel, s: number): void {
  const qs = Math.round((s < 0 ? 0 : s > 1 ? 1 : s) * 500) / 500
  if (qs === ch.a) return
  ch.a = qs
  ch.node.style.transform = `scaleX(${qs})`
}

function opacity(ch: Channel, v: number): void {
  const qv = q100(v < 0 ? 0 : v > 1 ? 1 : v)
  if (qv === ch.a) return
  ch.a = qv
  ch.node.style.opacity = String(qv)
}

function dashOffset(ch: Channel, v: number): void {
  const qv = q10(v)
  if (qv === ch.a) return
  ch.a = qv
  ch.node.style.strokeDashoffset = String(qv)
}

/** A text constant (tuning labels and literals: compared by identity, no string built per frame). */
function text(ch: Channel, s: string): void {
  if (s === ch.s) return
  ch.s = s
  ch.node.textContent = s
}

/** An integer readout. */
function integer(ch: Channel, v: number): void {
  const n = Math.round(v)
  if (n === ch.a) return
  ch.a = n
  ch.node.textContent = String(n)
}

/** A distance readout: metres to 5 m below 1 km, kilometres to 0.1 km below 1,000 km, then Mm and Gm (D-459). */
function distance(ch: Channel, metres: number): void {
  const key = hudDistanceKey(metres)
  if (key === ch.a) return
  ch.a = key
  ch.node.textContent = formatHudDistance(metres)
}

/** The speed numeral and its unit, both from the one shared formatter (H1); strings built only when the key changes. */
function speedReadout(num: Channel, unit: Channel, mps: number): void {
  const key = hudSpeedKey(mps)
  if (key === num.a) return
  num.a = key
  const r = formatHudSpeed(mps)
  text(num, r.value)
  text(unit, r.unit)
}

/** Closing speed: "closing 120 m/s" or "opening 40 m/s" (H4), keyed by the sign and the displayed speed. */
function closingSpeed(ch: Channel, mps: number): void {
  const opening = mps < 0
  const abs = opening ? -mps : mps
  const key = speedKey(abs)
  const sign = opening ? 1 : 0
  if (key === ch.a && sign === ch.b) return
  ch.a = key
  ch.b = sign
  const t = HUD_READOUTS.text
  ch.node.textContent = `${opening ? t.opening : t.closing} ${formatSpeed(abs)}`
}

function attr(ch: Channel, name: string, value: string): void {
  if (value === ch.s) return
  ch.s = value
  ch.node.setAttribute(name, value)
}

function flag(ch: Channel, name: string, on: boolean): void {
  attr(ch, name, on ? '1' : '0')
}

const HIT_NAMES = ['none', 'shield', 'hull', 'kill'] as const
const VV_NAMES = ['hidden', 'pro', 'retro'] as const

// ---- Frame ----------------------------------------------------------------------------------------------------

/** Writes `state` into the DOM through the diffed channels. Allocation-free while values are steady. */
export function renderHud(v: HudView, s: HudState, tuning: HudTuning): void {
  const a = v.attrs
  flag(a.alive, 'data-alive', s.alive)
  attr(a.view, 'data-view', s.cameraMode)
  const tagOn = v.debugTag()
  flag(a.cameraOn, 'data-on', tagOn)
  if (tagOn) text(v.camera, s.cameraMode === 'chase' ? v.chaseLabel : v.cockpitLabel)
  // Camera switch toast (H5): shown for `cameraToastSeconds`, fading out over the last `cameraToastFadeSeconds`.
  const toastLeft = HUD_READOUTS.cameraToastSeconds - s.cameraToastAge
  const toastOn = toastLeft > 0
  flag(a.cameraToastOn, 'data-on', toastOn)
  if (toastOn) {
    const toast = HUD_READOUTS.text.cameraToast
    text(v.cameraToast, s.cameraMode === 'chase' ? toast.chase : toast.cockpit)
    const fade = HUD_READOUTS.cameraToastFadeSeconds
    opacity(v.cameraToastOpacity, fade > 0 && toastLeft < fade ? toastLeft / fade : 1)
  }
  translate(v.stick, s.stickX * STICK_RADIUS_PX, -s.stickY * STICK_RADIUS_PX)

  // Status panels.
  scaleX(v.shieldFill, s.shield)
  scaleX(v.hullFill, s.hull)
  scaleX(v.energyFill, s.energy)
  integer(v.shieldValue, s.shield * 100)
  integer(v.hullValue, s.hull * 100)
  integer(v.energyValue, s.energy * 100)
  flag(a.hullLow, 'data-low', s.lowHull)
  flag(a.boost, 'data-boost', s.boosting)
  speedReadout(v.speed, v.speedUnit, s.speed)
  flag(a.faOn, 'data-fa', s.faOn)
  text(v.fa, s.faOn ? HUD_READOUTS.text.faOn : HUD_READOUTS.text.faOff)
  text(v.weaponSlot, s.weaponSlot === 0 ? '1' : '2')
  text(v.weaponName, s.weaponLabel)
  flag(a.secondaryOn, 'data-on', s.hasTorpedo)
  integer(v.torpedoes, s.torpedoes)
  const lockState = s.locked ? 'locked' : s.lockProgress > 0 ? 'building' : 'off'
  attr(a.lockStatus, 'data-state', lockState)
  text(v.lockStatus, s.locked ? 'LOCKED' : s.lockProgress > 0 ? 'LOCKING' : s.target.eid >= 0 ? 'NO LOCK' : 'NO TARGET')

  // Wave, enemies, banner, respawn.
  if (s.wave !== v.wave.a) {
    // Wave changes are rare: build both strings then.
    v.wave.a = s.wave
    v.el.wave.textContent = s.wave > 0 ? `WAVE ${s.wave}` : 'STANDBY'
    v.el.banner.textContent = s.wave > 0 ? `WAVE ${s.wave}` : ''
  }
  integer(v.enemies, s.enemies)
  const bannerOn = s.wave > 0 && s.waveAge < tuning.waveBannerSeconds
  flag(a.bannerOn, 'data-on', bannerOn)
  if (bannerOn) {
    const rest = tuning.waveBannerSeconds - s.waveAge
    opacity(v.bannerOpacity, rest < 1 ? rest : s.waveAge < 0.25 ? s.waveAge * 4 : 1)
  }
  flag(a.respawnOn, 'data-on', !s.alive)
  if (!s.alive) {
    const tenths = Math.ceil(s.respawnIn * 10)
    if (tenths !== v.respawn.a) {
      v.respawn.a = tenths
      v.el.respawn.textContent = `SHIP LOST · RESPAWN ${(tenths / 10).toFixed(1)}`
    }
  }

  // Centre: boresight, velocity vector, hit markers.
  const bs = s.boresight
  translate(v.boresight, bs.inFront ? bs.x : -100, bs.inFront ? bs.y : -100)
  const assistOn = bs.inFront && s.assistRadiusPx > 0
  flag(a.assistOn, 'data-on', assistOn)
  if (assistOn) translateScale(v.assist, bs.x, bs.y, s.assistRadiusPx / ASSIST_SVG_RADIUS)
  attr(a.velocityMode, 'data-mode', VV_NAMES[s.velocityMode])
  if (s.velocityMode !== VV.hidden) translate(v.velocity, s.velocity.x, s.velocity.y)
  const hitOn = s.hitAge < tuning.hitMarkerSeconds && s.hitKind !== HIT.none
  attr(a.hitKind, 'data-kind', hitOn ? HIT_NAMES[s.hitKind] : 'none')
  if (hitOn) {
    const k = s.hitAge / tuning.hitMarkerSeconds
    translateScale(v.hit, bs.x, bs.y, 1.35 - 0.35 * k)
    opacity(v.hitOpacity, 1 - k * k)
  }

  // Target: bracket (on screen) or edge arrow (off screen), lead pip and lock ring.
  const t = s.target
  const hasTarget = t.eid >= 0
  const onScreen = hasTarget && t.point.onScreen
  flag(a.targetOn, 'data-on', onScreen)
  flag(a.arrowOn, 'data-on', hasTarget && !onScreen)
  // Compact target line in the weapons block (H4), on or off screen.
  flag(a.tgtOn, 'data-on', hasTarget)
  if (hasTarget) {
    text(v.tgtName, t.name)
    distance(v.tgtDistance, t.distance)
  }
  if (hasTarget) {
    const hostile = t.hostile
    flag(a.targetHostile, 'data-hostile', hostile)
    flag(a.arrowHostile, 'data-hostile', hostile)
    if (onScreen) renderBracket(v, s, hostile)
    else {
      translate(v.arrow, t.point.edgeX, t.point.edgeY)
      rotate(v.arrowHead, t.point.angle * RAD_TO_DEG)
      distance(v.arrowDistance, t.distance)
    }
  }
  const leadOn = hasTarget && onScreen && t.leadVisible
  flag(a.leadOn, 'data-on', leadOn)
  if (leadOn) {
    flag(a.leadGimbal, 'data-gimbal', t.leadInGimbal)
    translate(v.lead, t.lead.x, t.lead.y)
  }
  const engaged = leadOn ? t.assist : 0
  attr(a.leadAssist, 'data-assist', ASSIST_NAMES[engaged])
  attr(a.assistEngaged, 'data-engaged', ASSIST_NAMES[engaged])

  // Contacts: every other visible ship.
  for (let i = 0; i < v.contacts.length; i++) {
    const on = i < s.contactCount
    flag(a.contactOn[i], 'data-on', on)
    if (!on) continue
    flag(a.contactHostile[i], 'data-hostile', s.contactHostile[i] === 1)
    translate(v.contacts[i], s.contactX[i], s.contactY[i])
  }

  // Damage-direction wedges.
  const n = v.wedges.length < WEDGE_COUNT ? v.wedges.length : WEDGE_COUNT
  for (let i = 0; i < n; i++) {
    const w = s.wedges[i]
    const on = w.active && w.intensity > 0.005
    attr(a.wedgeKind[i], 'data-kind', on ? (w.kind === HIT.shield ? 'shield' : 'hull') : 'none')
    if (!on) continue
    rotateScale(v.wedges[i], w.angle * RAD_TO_DEG, tuning.wedge.radiusPx / WEDGE_ART_RADIUS)
    opacity(v.wedgeOpacity[i], w.intensity)
  }
}

/** Bracket pieces, info block and lock ring around an on-screen target. */
function renderBracket(v: HudView, s: HudState, hostile: boolean): void {
  const t = s.target
  const h = t.halfPx
  translate(v.target, t.point.x, t.point.y)
  if (hostile) {
    // A diamond of inward chevrons: top, right, bottom, left.
    const k = h * 1.3
    translateRotate(v.corners[0], 0, -k, 45)
    translateRotate(v.corners[1], k, 0, 135)
    translateRotate(v.corners[2], 0, k, 225)
    translateRotate(v.corners[3], -k, 0, 315)
  } else {
    // Square corners: top-left, top-right, bottom-right, bottom-left.
    translateRotate(v.corners[0], -h, -h, 0)
    translateRotate(v.corners[1], h, -h, 90)
    translateRotate(v.corners[2], h, h, 180)
    translateRotate(v.corners[3], -h, h, 270)
  }
  translate(v.targetInfo, 0, (hostile ? h * 1.3 : h) + INFO_GAP_PX)
  distance(v.targetDistance, t.distance)
  // Target card (H4): the meta line is rebuilt only when the class, the faction or the stance changes.
  text(v.targetName, t.name)
  const meta = v.targetMeta
  if (t.stance !== meta.a || t.faction !== meta.s || t.cls !== v.metaCls) {
    meta.a = t.stance
    meta.s = t.faction
    v.metaCls = t.cls
    const glyph = `${STANCE_GLYPHS[t.stance]} ${t.faction}`
    meta.node.textContent = t.cls.length > 0 ? `${t.cls}${HUD_READOUTS.text.separator}${glyph}` : glyph
  }
  closingSpeed(v.targetClosing, t.closing)
  scaleX(v.targetShield, t.shield)
  scaleX(v.targetHull, t.hull)
  const lockState = s.locked ? 'locked' : s.lockProgress > 0 ? 'building' : 'off'
  attr(v.attrs.lock, 'data-lock', lockState)
  if (lockState !== 'off') {
    scale(v.lockRing, (h * (hostile ? 1.3 : 1.42) + 12) / LOCK_R)
    dashOffset(v.lockArc, LOCK_C * (1 - s.lockProgress))
  }
}

// ---- DOM ------------------------------------------------------------------------------------------------------

const BAR = (id: string, label: string): string => `
  <div class="hud-stat" id="hud-${id}-bar">
    <span class="hud-stat-label">${label}</span>
    <span class="hud-stat-track"><span class="hud-stat-fill" id="hud-${id}-fill"></span></span>
    <span class="hud-stat-value" id="hud-${id}-value">100</span>
  </div>`

function markup(maxContacts: number): string {
  let contacts = ''
  for (let i = 0; i < maxContacts; i++) contacts += '<div class="hud-contact hud-abs" data-on="0" data-hostile="1"><i></i></div>'
  let wedges = ''
  for (let i = 0; i < WEDGE_COUNT; i++) {
    wedges +=
      '<svg class="hud-wedge" viewBox="-170 -170 340 340" data-kind="none" aria-hidden="true">' +
      '<path class="hud-wedge-solid" d="M -52.4 -143.8 A 153 153 0 0 1 52.4 -143.8 L 47.9 -131.6 A 140 140 0 0 0 -47.9 -131.6 Z"/>' +
      '<path class="hud-wedge-line" d="M -52.4 -143.8 A 153 153 0 0 1 52.4 -143.8 M 47.9 -131.6 A 140 140 0 0 0 -47.9 -131.6"/>' +
      '</svg>'
  }
  return `
  <div class="hud-root" data-alive="0" data-view="chase">
    <div class="hud-centre">
      <div class="hud-stickring"></div>
      <div class="hud-stickdot" id="hud-stick-dot"></div>
      <div class="hud-wedges">${wedges}</div>
    </div>
    <div class="hud-layer hud-combat">
      ${contacts}
      <svg class="hud-velocity hud-abs" id="hud-velocity" viewBox="-16 -16 32 32" data-mode="hidden" aria-hidden="true">
        <g class="hud-vv-pro"><circle r="6"/><path d="M -6 0 H -13 M 6 0 H 13 M 0 -6 V -12"/></g>
        <g class="hud-vv-retro"><circle r="6"/><path d="M -4.2 -4.2 L 4.2 4.2 M 4.2 -4.2 L -4.2 4.2 M -6 0 H -11 M 6 0 H 11 M 0 6 V 11"/></g>
      </svg>
      <svg class="hud-assist hud-abs" id="hud-assist" viewBox="-50 -50 100 100" data-on="0" data-engaged="0" aria-hidden="true">
        <circle class="hud-assist-ring" r="48"/>
      </svg>
      <svg class="hud-boresight hud-abs" id="hud-boresight" viewBox="-20 -20 40 40" aria-hidden="true">
        <path d="M -16 0 H -6 M 6 0 H 16 M 0 -16 V -6 M 0 6 V 16"/><circle r="1.4"/>
      </svg>
      <svg class="hud-hit hud-abs" id="hud-hit" viewBox="-20 -20 40 40" data-kind="none" aria-hidden="true">
        <path d="M -13 -13 L -6 -6 M 13 -13 L 6 -6 M 13 13 L 6 6 M -13 13 L -6 6"/>
      </svg>
      <div class="hud-target hud-abs" id="hud-target" data-on="0" data-hostile="1" data-lock="off">
        <i class="hud-corner"></i><i class="hud-corner"></i><i class="hud-corner"></i><i class="hud-corner"></i>
        <svg class="hud-lockring" id="hud-lockring" viewBox="-50 -50 100 100" aria-hidden="true">
          <circle class="hud-lock-track" r="${LOCK_R}"/>
          <circle class="hud-lock-arc" id="hud-lock-arc" r="${LOCK_R}" transform="rotate(-90)"
            stroke-dasharray="${LOCK_C.toFixed(2)}" style="stroke-dashoffset:${LOCK_C.toFixed(2)}"/>
          <path class="hud-lock-ticks" d="M 0 -50 V -40 M 50 0 H 40 M 0 50 V 40 M -50 0 H -40"/>
        </svg>
        <div class="hud-tinfo" id="hud-tinfo">
          <div class="hud-tname" id="hud-target-name"></div>
          <div class="hud-tmeta" id="hud-target-meta"></div>
          <div class="hud-tdist" id="hud-target-distance">0 m</div>
          <div class="hud-tclose" id="hud-target-closing"></div>
          <div class="hud-tbar hud-tbar-shield"><span id="hud-target-shield"></span></div>
          <div class="hud-tbar hud-tbar-hull"><span id="hud-target-hull"></span></div>
        </div>
      </div>
      <svg class="hud-lead hud-abs" id="hud-lead" viewBox="-12 -12 24 24" data-on="0" data-gimbal="0" aria-hidden="true">
        <circle class="hud-lead-ring" r="8"/><circle class="hud-lead-dot" r="2.6"/>
      </svg>
      <div class="hud-arrow hud-abs" id="hud-arrow" data-on="0" data-hostile="1">
        <svg class="hud-arrow-head" id="hud-arrow-head" viewBox="-16 -16 32 32" aria-hidden="true">
          <path d="M 0 -13 L 11 6 L 0 1 L -11 6 Z"/>
        </svg>
        <div class="hud-arrow-dist" id="hud-arrow-distance"></div>
      </div>
    </div>
    <div class="hud-top">
      <span class="hud-wave" id="hud-wave">STANDBY</span>
      <span class="hud-sep"></span>
      <span class="hud-enemies"><span class="hud-stat-label">HOSTILES</span> <b id="hud-enemies">0</b></span>
    </div>
    <div class="hud-banner" id="hud-banner" data-on="0"></div>
    <div class="hud-respawn" id="hud-respawn" data-on="0"></div>
    <div class="hud-dock hud-dock-left hud-combat">
      ${BAR('shield', 'SHD')}
      ${BAR('hull', 'HUL')}
      ${BAR('energy', 'NRG')}
      <div class="hud-speedrow">
        <span class="hud-speed" id="hud-speed">0</span><span class="hud-unit" id="hud-speed-unit">m/s</span>
        <span class="hud-fa" id="hud-fa" data-fa="1">FA ON</span>
      </div>
    </div>
    <div class="hud-dock hud-dock-right hud-combat">
      <div class="hud-weapon"><span class="hud-slot" id="hud-weapon-slot">1</span><span class="hud-wname" id="hud-weapon-name"></span></div>
      <div class="hud-secondary" id="hud-secondary" data-on="0">
        <span class="hud-stat-label">TORP</span> <b id="hud-torpedoes">0</b>
        <span class="hud-lockstatus" id="hud-lock-status" data-state="off">NO TARGET</span>
      </div>
      <div class="hud-tgt" id="hud-tgt" data-on="0">
        <span class="hud-stat-label">${HUD_READOUTS.text.targetPrefix}</span>
        <span class="hud-tgt-name" id="hud-tgt-name"></span>${HUD_READOUTS.text.separator}<span id="hud-tgt-distance"></span>
      </div>
    </div>
    <div class="hud-meta" id="hud-camera" data-on="0"></div>
    <div class="hud-camtoast" id="hud-camtoast" data-on="0"></div>
  </div>`
}

/** Builds the HUD markup inside `root` (replacing the M0 HUD) and returns the elements the view writes. Init only. */
export function mountHud(root: HTMLElement, maxContacts: number): HudElements {
  root.innerHTML = markup(maxContacts)
  const q = <T extends Element>(sel: string): T => {
    const el = root.querySelector<T>(sel)
    if (!el) throw new Error(`HUD element ${sel} missing`)
    return el as T
  }
  const all = (sel: string): HTMLElement[] => Array.from(root.querySelectorAll<HTMLElement>(sel))
  const svgAll = (sel: string): SVGElement[] => Array.from(root.querySelectorAll<SVGElement>(sel))
  return {
    root: q<HTMLElement>('.hud-root'),
    stickDot: q<HTMLElement>('#hud-stick-dot'),
    boresight: q<SVGElement>('#hud-boresight'),
    assist: q<SVGElement>('#hud-assist'),
    hit: q<SVGElement>('#hud-hit'),
    velocity: q<SVGElement>('#hud-velocity'),
    lead: q<SVGElement>('#hud-lead'),
    target: q<HTMLElement>('#hud-target'),
    corners: all('#hud-target .hud-corner'),
    lockRing: q<SVGElement>('#hud-lockring'),
    lockArc: q<SVGElement>('#hud-lock-arc'),
    targetInfo: q<HTMLElement>('#hud-tinfo'),
    targetDistance: q<HTMLElement>('#hud-target-distance'),
    targetShield: q<HTMLElement>('#hud-target-shield'),
    targetHull: q<HTMLElement>('#hud-target-hull'),
    arrow: q<HTMLElement>('#hud-arrow'),
    arrowHead: q<SVGElement>('#hud-arrow-head'),
    arrowDistance: q<HTMLElement>('#hud-arrow-distance'),
    contacts: all('.hud-contact'),
    wedges: svgAll('.hud-wedge'),
    shieldFill: q<HTMLElement>('#hud-shield-fill'),
    shieldValue: q<HTMLElement>('#hud-shield-value'),
    hullBar: q<HTMLElement>('#hud-hull-bar'),
    hullFill: q<HTMLElement>('#hud-hull-fill'),
    hullValue: q<HTMLElement>('#hud-hull-value'),
    energyBar: q<HTMLElement>('#hud-energy-bar'),
    energyFill: q<HTMLElement>('#hud-energy-fill'),
    energyValue: q<HTMLElement>('#hud-energy-value'),
    speed: q<HTMLElement>('#hud-speed'),
    fa: q<HTMLElement>('#hud-fa'),
    weaponSlot: q<HTMLElement>('#hud-weapon-slot'),
    weaponName: q<HTMLElement>('#hud-weapon-name'),
    secondary: q<HTMLElement>('#hud-secondary'),
    torpedoes: q<HTMLElement>('#hud-torpedoes'),
    lockStatus: q<HTMLElement>('#hud-lock-status'),
    wave: q<HTMLElement>('#hud-wave'),
    enemies: q<HTMLElement>('#hud-enemies'),
    banner: q<HTMLElement>('#hud-banner'),
    respawn: q<HTMLElement>('#hud-respawn'),
    camera: q<HTMLElement>('#hud-camera'),
    speedUnit: q<HTMLElement>('#hud-speed-unit'),
    cameraToast: q<HTMLElement>('#hud-camtoast'),
    targetName: q<HTMLElement>('#hud-target-name'),
    targetMeta: q<HTMLElement>('#hud-target-meta'),
    targetClosing: q<HTMLElement>('#hud-target-closing'),
    tgt: q<HTMLElement>('#hud-tgt'),
    tgtName: q<HTMLElement>('#hud-tgt-name'),
    tgtDistance: q<HTMLElement>('#hud-tgt-distance'),
  }
}
